import { Box, Grid, Stack, Typography, Radio, Button } from "@mui/material";
import { TextareaAutosize } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DynamicAttachmentView from "../UI/DynamicAttachmentView";
import MilestoneMultiForm from "../UI/MilestoneMultiForm";
import ByProjectForm from "../UI/ByProjectForm";
import { SERVICE_FEE_RATE } from "../UI/priceUtils";
import { tokens } from "../../store/tokens";

const payOptions = [
  {
    value: "milestone",
    label: "By milestone",
    description:
      "Divide the project into smaller segments. You'll be paid for milestones as they are completed and approved.",
  },
  {
    value: "project",
    label: "By project",
    description: "Get your entire payment at the end, when all work has been delivered.",
  },
  {
    value: "hourly",
    label: "By hourly",
    description: "Get paid for the hours you log on this job.",
  },
];

const inputSx = {
  borderRadius: `${tokens.input.radius}px`,
  bgcolor: tokens.input.bg,
  fontSize: tokens.input.fontSize.sm,
  "& fieldset": { borderColor: tokens.input.border },
  "&:hover fieldset": { borderColor: tokens.input.borderHover },
  "&.Mui-focused fieldset": { borderColor: tokens.input.borderFocus },
};

const ApplyForm = ({ user, isMember, fetchJobDetails, setIsApplication, handleSnackOpen }) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    register,
  } = useForm({
    defaultValues: { t_comment: "", payType: "milestone" },
  });

  const navigate = useNavigate();
  const [cpInfo, setCpInfo] = useState({});
  const [attachmentList, setAttachmentList] = useState([]);
  const [fileRefer, setFileRefer] = useState("");
  const [payType, setPayType] = useState("milestone");
  const [gcTagList, setGcTagList] = useState([]);

  const jobRefer = fetchJobDetails?.job_refer;
  const jobId = fetchJobDetails?.gid;

  useEffect(() => {
    setCpInfo((prev) => ({ ...prev, attachment: attachmentList }));
  }, [attachmentList]);

  const handlePayTypeChange = (selected) => {
    setPayType(selected);
    if (selected === "milestone") {
      setCpInfo((prev) => ({
        ...prev,
        budget: "",
        budgetCharge: "",
        clientPrice: "",
        milestones: prev.milestones || [],
      }));
    } else {
      setCpInfo((prev) => ({ ...prev, milestones: [] }));
    }
  };

  // Budget input for the "by project"/"by hourly" flow. Uses the same
  // SERVICE_FEE_RATE as ByProjectForm's own calculation, so the number
  // shown here and the number ByProjectForm computes can't disagree.
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "budget") {
      const isValid = /^[0-9]+$/.test(value);
      if (isValid) {
        const charge = Math.round(value * SERVICE_FEE_RATE);
        const clientPrice = Math.round(parseFloat(value) - charge);
        setCpInfo((prev) => ({ ...prev, budget: value, budgetCharge: charge, clientPrice }));
      }
    } else if (/[a-zA-Z0-9]/.test(value)) {
      setCpInfo((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onSubmit = (data) => {
    const formData = new FormData();
    const milestones = cpInfo?.milestones || [];

    const bid = cpInfo?.budget
      ? cpInfo.budget
      : Math.round(milestones.reduce((acc, el) => acc + parseFloat(el?.ms_amount || 0), 0));

    const baseAmount = milestones.reduce((acc, el) => acc + parseFloat(el?.ms_amount || 0), 0);
    const gh_charge = cpInfo?.budgetCharge || Math.round(baseAmount * SERVICE_FEE_RATE);
    const bid_amount = cpInfo?.clientPrice || Math.round(baseAmount - baseAmount * SERVICE_FEE_RATE);

    formData.append("proposalRefer", fileRefer ?? "");
    formData.append("gobId", jobId);
    formData.append("grefer", jobRefer);
    formData.append("poster_id", fetchJobDetails?.poster_id);
    formData.append("njobName", fetchJobDetails?.title);
    formData.append("invitation_type", "JobOffer");
    formData.append("userId", user?.sopnoid);
    formData.append("bid", bid);
    formData.append("bidCurrency", fetchJobDetails?.currency_id);
    formData.append("workAs", 1);
    formData.append("workSchedule", 1);
    formData.append("user_number", user?.umobile);
    formData.append("coverLetter", data.t_comment);
    formData.append("preWork", JSON.stringify(cpInfo?.attachment || []));
    formData.append("mile_stone", JSON.stringify(milestones));
    formData.append("poster", user?.sopnoid);
    formData.append("cRefer", user?.c_refer);
    formData.append("gh_charge", gh_charge);
    formData.append("bid_amount", bid_amount);

    fetch("https://aps.songjukti.com/profile/login/api_form/job_apply_new.php", {
      method: "POST",
      body: formData,
    })
      .then((res) => res.json())
      .then((result) => {
        if (result?.m_type === "success") {
          setFileRefer("");
          setAttachmentList([]);
          setGcTagList([]);
          handleSnackOpen(result?.m_result ?? "Job Applied Successfully.");
          navigate("/joblist");
        } else {
          console.error("API Failed:", result);
        }
      })
      .catch((error) => console.error("API Error:", error));
  };

  const coverLetter = watch("t_comment");
  const canSubmit = coverLetter && (cpInfo?.budget || cpInfo?.milestones?.length > 0);

  return (
    <Stack spacing={1} sx={{ padding: "0px", mt: "10px" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontFamily: tokens.font.body, fontSize: "1rem", fontWeight: 700, color: tokens.color.ink }}>
              Apply here
            </Typography>
            <Typography sx={{ fontFamily: tokens.font.body, fontSize: "0.85rem", fontWeight: 600, color: tokens.color.inkMuted }}>
              Budget: {fetchJobDetails?.budget ?? "-"} {fetchJobDetails?.currency?.[0]?.title ?? "BDT"}
            </Typography>
          </Stack>

          <Typography
            sx={{
              fontFamily: tokens.font.body,
              fontSize: "0.9rem",
              fontWeight: 700,
              color: tokens.color.ink,
              borderTop: `1px solid ${tokens.color.line}`,
              py: "12px",
              mt: 2,
            }}
          >
            How do you want to be paid?
          </Typography>

          <Controller
            control={control}
            name="payType"
            render={({ field }) => (
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {payOptions.map((opt) => {
                  const selected = payType === opt.value;
                  return (
                    <Box
                      key={opt.value}
                      role="button"
                      onClick={() => {
                        field.onChange(opt.value);
                        handlePayTypeChange(opt.value);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        p: 1.5,
                        border: `1px solid ${selected ? tokens.color.teal : tokens.color.line}`,
                        borderRadius: `${tokens.radius.md}px`,
                        bgcolor: selected ? tokens.color.tealLight : tokens.bg.surface,
                        cursor: "pointer",
                        transition: "border-color 0.15s ease, background-color 0.15s ease",
                        "&:hover": { borderColor: tokens.color.teal },
                      }}
                    >
                      <Radio
                        checked={selected}
                        size="small"
                        sx={{
                          p: 0,
                          mt: "1px",
                          color: tokens.color.inkFaint,
                          "&.Mui-checked": { color: tokens.color.teal },
                        }}
                      />
                      <Stack spacing={0.25}>
                        <Typography sx={{ fontFamily: tokens.font.body, fontSize: "0.9rem", fontWeight: 700, color: tokens.color.ink }}>
                          {opt.label}
                        </Typography>
                        <Typography sx={{ fontFamily: tokens.font.body, fontSize: "0.8rem", color: tokens.color.inkMuted }}>
                          {opt.description}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          />

          {payType === "milestone" && (
            <Box mt={3}>
              <MilestoneMultiForm setCpInfo={setCpInfo} cpInfo={cpInfo} fetchJobDetails={fetchJobDetails} />
            </Box>
          )}

          {(payType === "project" || payType === "hourly") && (
            <Box mt={3}>
              <ByProjectForm
                setCpInfo={setCpInfo}
                cpInfo={cpInfo}
                fetchJobDetails={fetchJobDetails}
                handleChange={handleChange}
                hourly={payType === "hourly" ? "hourly" : ""}
              />
            </Box>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography sx={{ fontFamily: tokens.font.body, fontSize: "0.85rem", fontWeight: 600, color: tokens.color.ink }}>
              Cover letter
            </Typography>
            <TextareaAutosize
              minRows={3}
              placeholder="Describe why you're a great fit for this job"
              style={{
                fontFamily: tokens.font.body,
                fontSize: "0.85rem",
                width: "100%",
                boxSizing: "border-box",
                border: `1px solid ${tokens.input.border}`,
                borderRadius: `${tokens.input.radius}px`,
                padding: "10px 14px",
                marginTop: "6px",
                color: tokens.color.ink,
              }}
              {...register("t_comment", { required: "Cover letter is required" })}
            />
            {errors.t_comment && (
              <Typography sx={{ color: tokens.color.error, fontSize: "0.75rem", mt: 0.5 }}>
                {errors.t_comment.message}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ fontFamily: tokens.font.body, fontSize: "0.85rem", fontWeight: 600, color: tokens.color.ink }}>
              Attachment (PDF/DOC — max 2)
            </Typography>
            <DynamicAttachmentView
              label=""
              itemPicName="proposalAttach"
              itemType="new"
              MAX_FILE_SIZE_MB={5}
              MAX_FILES={2}
              user={user}
              croom={fileRefer}
              fileRefer={fileRefer}
              setFileRefer={setFileRefer}
              attachmentList={attachmentList}
              setAttachmentList={setAttachmentList}
              allowedTypes={[
                "application/pdf",
                "application/x-zip-compressed",
                "application/postscript",
                "text/plain",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "video/mp4",
              ]}
              onGcTag={(gc_tag) => setGcTagList((prev) => [...prev, gc_tag])}
            />
            <Typography sx={{ fontFamily: tokens.font.body, fontSize: "0.78rem", color: tokens.color.inkMuted, pt: 1 }}>
              Attach up to 2 files under 10 MB each. Include work samples or supporting documents.
            </Typography>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} justifyContent="flex-start" sx={{ mt: 4, mb: 6 }}>
          <Button
            variant="outlined"
            onClick={() => setIsApplication(false)}
            sx={{
              borderColor: tokens.color.line,
              color: tokens.color.ink,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: `${tokens.radius.pill}px`,
              "&:hover": { borderColor: tokens.color.teal, color: tokens.color.teal, bgcolor: tokens.color.tealLight },
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            sx={{
              bgcolor: tokens.color.teal,
              color: tokens.color.white,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: `${tokens.radius.pill}px`,
              "&:hover": { bgcolor: tokens.color.tealDark },
            }}
          >
            Send
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};

export default ApplyForm;